import { useMemo, useState } from 'react';
import {
  Surface,
  dangerButtonStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../lib/use-auth.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import {
  recruitersRepo,
  subscribeRecruiterApplicationsForBrand,
  subscribeRecruitersForBrand,
  type RecruiterApplication,
  type RecruiterProfile,
} from '../lib/repositories/recruiters-repo.js';
import { KycScanLink } from '../components/kyc-scan-link.js';
import {
  callApproveRecruiterApplication,
  callRejectRecruiterApplication,
} from '../lib/callables/recruiters.js';
import { logger } from '../lib/logger.js';

/**
 * /superadmin/recruiters — Sprint 82 moderation page for the recruiter
 * epic (4th-layer persona).
 *
 * Access: gated to `claims.superadmin[currentBrandId] === true` (ADR-0017).
 * Recruiter signup is NOT instant-activation (unlike agents) — applicants
 * land in `recruiterApplications` with `status: 'pending'` and a super-admin
 * approves them here. Approval mints the Auth user, generates the `RC-XXXX`
 * code, writes the recruiter doc, and sets the `recruiter: true` custom
 * claim. Rejection is permanent and writes an audit reason.
 *
 * UX shape (mirrors `/superadmin/agents` so muscle memory transfers):
 *   - Top: pending-queue list with one-tap Approve / Reject
 *   - Bottom: roster of already-approved recruiters with their codes
 *
 * Every state has an explicit surface per feedback-no-dead-ends:
 *   - loading, error, empty-queue ("you're caught up"), success-with-code
 *     after approve, dismissable rejection-reason inline form.
 */
export function SuperadminRecruitersPage() {
  const { user, claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();

  const isSuperAdmin = isSuperAdminForBrand(claims, brand.id);

  const appsSubscribe = useMemo(() => {
    if (!isSuperAdmin) return null;
    return (cb: (vs: RecruiterApplication[]) => void) =>
      subscribeRecruiterApplicationsForBrand(brand.id, cb);
  }, [isSuperAdmin, brand.id]);
  const {
    data: apps,
    loading: appsLoading,
    error: appsError,
  } = useTrackedSnapshot<RecruiterApplication[]>(
    ['superadmin-recruiter-applications', brand.id],
    appsSubscribe,
  );

  const recruitersSubscribe = useMemo(() => {
    if (!isSuperAdmin) return null;
    return (cb: (vs: RecruiterProfile[]) => void) => subscribeRecruitersForBrand(brand.id, cb);
  }, [isSuperAdmin, brand.id]);
  const { data: recruiters, loading: recruitersLoading } = useTrackedSnapshot<RecruiterProfile[]>(
    ['superadmin-recruiters', brand.id],
    recruitersSubscribe,
  );

  if (authLoading) return <Screen title="Signing you in…" body="One second." />;
  if (!user) {
    return (
      <Screen
        title="You're signed out."
        body={`Sign in as a super-admin for ${brand.name} to moderate recruiter applications.`}
        cta={{ label: 'Back to queue', to: '/queue' }}
      />
    );
  }
  if (!isSuperAdmin) {
    return (
      <Screen
        title="You're not a super-admin here."
        body={`Your account isn't a super-admin for ${brand.name}. If that's wrong, ask the platform owner to grant access.`}
        cta={{ label: 'Back to queue', to: '/queue' }}
      />
    );
  }

  const allApps = (apps ?? []).filter((a) => a.platformBrandId === brand.id);
  const pending = allApps
    .filter((a) => a.status === 'pending')
    .slice()
    .sort((a, b) => tsMs(a.submittedAt) - tsMs(b.submittedAt));
  const recentlyHandled = allApps
    .filter((a) => a.status !== 'pending')
    .slice()
    .sort((a, b) => tsMs(b.reviewedAt) - tsMs(a.reviewedAt))
    .slice(0, 8);

  const liveRecruiters = (recruiters ?? [])
    .filter((r) => r.platformBrandId === brand.id)
    .slice()
    .sort((a, b) => tsMs(b.createdAt) - tsMs(a.createdAt));

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
        }}
      >
        <div>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              letterSpacing: 4,
              textTransform: 'uppercase',
              margin: 0,
            }}
          >
            {brand.name} · Super-admin
          </p>
          <h1
            style={{
              fontSize: 'var(--text-h1)',
              fontWeight: 600,
              margin: 'var(--space-2) 0 0 0',
            }}
          >
            Recruiters
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 0,
            }}
          >
            {appsLoading
              ? 'Loading queue…'
              : `${pending.length} pending · ${liveRecruiters.length} active on ${brand.name}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Link to="/superadmin/agents" style={ghostLinkStyle}>
            Agents
          </Link>
          <Link to="/queue" style={ghostLinkStyle}>
            Back to queue
          </Link>
        </div>
      </header>

      <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
        {/* ── Pending queue ────────────────────────────────────────── */}
        <Surface as="section">
          <SectionHeader
            title="Pending applications"
            hint="Each new application from /become-a-recruiter lands here. Approve to mint the recruiter + RC-XXXX code, reject to close it out (optionally with a reason for our audit trail)."
          />
          {appsLoading ? (
            <InlineNote>Loading queue… (live)</InlineNote>
          ) : appsError ? (
            <InlineNote tone="error">
              Couldn't load applications: {String(appsError.message ?? appsError)}. Refresh to
              retry.
            </InlineNote>
          ) : pending.length === 0 ? (
            <EmptyState
              title="You're caught up."
              body={`No pending recruiter applications on ${brand.name} right now. They'll show up here the moment someone submits at /become-a-recruiter/signup.`}
            />
          ) : (
            <ul
              style={{
                listStyle: 'none',
                margin: 'var(--space-4) 0 0 0',
                padding: 0,
                display: 'grid',
                gap: 'var(--space-3)',
              }}
            >
              {pending.map((app) => (
                <li key={app.id}>
                  <PendingApplicationCard app={app} />
                </li>
              ))}
            </ul>
          )}
        </Surface>

        {/* ── Recently handled ─────────────────────────────────────── */}
        {recentlyHandled.length > 0 ? (
          <Surface as="section">
            <SectionHeader
              title="Recently reviewed"
              hint="Last few approve / reject decisions. Approved rows live forever in /superadmin/recruiters/{id} once that detail page ships; rejected rows stay here as the audit record."
            />
            <ul
              style={{
                listStyle: 'none',
                margin: 'var(--space-4) 0 0 0',
                padding: 0,
                display: 'grid',
                gap: 'var(--space-2)',
              }}
            >
              {recentlyHandled.map((app) => (
                <li key={app.id} style={reviewedRowStyle}>
                  <div style={{ display: 'grid' }}>
                    <span style={{ fontWeight: 500 }}>{app.name}</span>
                    <span
                      style={{
                        fontSize: 'var(--text-meta)',
                        color: 'var(--color-on-surface-muted)',
                      }}
                    >
                      {app.email} · {app.phone} ·{' '}
                      {app.reviewedAt ? `reviewed ${formatDate(tsMs(app.reviewedAt))}` : '—'}
                      {app.rejectionReason ? ` · "${app.rejectionReason}"` : ''}
                    </span>
                  </div>
                  <StatusPill status={app.status} />
                </li>
              ))}
            </ul>
          </Surface>
        ) : null}

        {/* ── Active roster ────────────────────────────────────────── */}
        <Surface as="section">
          <SectionHeader
            title="Active recruiters"
            hint="Approved recruiters on this brand. Each carries their own RC-XXXX code that agents enter at signup to attribute lifetime recruiter commissions."
          />
          {recruitersLoading ? (
            <InlineNote>Loading roster… (live)</InlineNote>
          ) : liveRecruiters.length === 0 ? (
            <EmptyState
              title="No recruiters approved yet."
              body="Once you approve a pending application above, the new recruiter appears here with their RC code."
            />
          ) : (
            <ul
              style={{
                listStyle: 'none',
                margin: 'var(--space-4) 0 0 0',
                padding: 0,
                display: 'grid',
                gap: 'var(--space-2)',
              }}
            >
              {liveRecruiters.map((r) => (
                <li key={r.id} style={recruiterRowStyle}>
                  <div
                    style={{
                      display: 'grid',
                      minWidth: 0,
                      gap: 'var(--space-2)',
                      flex: '1 1 auto',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: 'var(--space-3)',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span
                        style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline' }}
                      >
                        <strong>{r.name}</strong>
                        <code
                          style={{
                            fontSize: 'var(--text-meta)',
                            color: 'var(--color-on-surface-muted)',
                            fontFamily: 'ui-monospace, monospace',
                          }}
                        >
                          {r.code}
                        </code>
                      </span>
                      <RecruiterStatusPill status={r.status} />
                    </div>
                    <span
                      style={{
                        fontSize: 'var(--text-meta)',
                        color: 'var(--color-on-surface-muted)',
                      }}
                    >
                      {r.email} · {r.phone} · tier {r.tier} · joined {formatDate(tsMs(r.createdAt))}
                    </span>
                    <RecruiterKycRow recruiterId={r.id} kyc={r.kyc} currentUserUid={user.uid} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Surface>
      </div>
    </main>
  );
}

// ── Pending application card ─────────────────────────────────────────

type CardStage =
  | { kind: 'idle' }
  | { kind: 'approving' }
  | { kind: 'approved'; recruiterId: string; code: string }
  | { kind: 'rejecting' }
  | { kind: 'rejected' }
  | { kind: 'showRejectForm' }
  | { kind: 'error'; message: string };

function PendingApplicationCard({ app }: { readonly app: RecruiterApplication }) {
  const [stage, setStage] = useState<CardStage>({ kind: 'idle' });
  const [rejectReason, setRejectReason] = useState('');

  async function approve() {
    setStage({ kind: 'approving' });
    try {
      const result = await callApproveRecruiterApplication({ applicationId: app.id });
      setStage({ kind: 'approved', recruiterId: result.recruiterId, code: result.code });
    } catch (e) {
      logger.error('approveRecruiterApplication failed', {
        applicationId: app.id,
        error: String(e),
      });
      setStage({
        kind: 'error',
        message: String((e as Error).message ?? 'Approval failed.'),
      });
    }
  }

  async function reject() {
    setStage({ kind: 'rejecting' });
    try {
      await callRejectRecruiterApplication({
        applicationId: app.id,
        ...(rejectReason.trim() ? { reason: rejectReason.trim() } : {}),
      });
      setStage({ kind: 'rejected' });
    } catch (e) {
      logger.error('rejectRecruiterApplication failed', {
        applicationId: app.id,
        error: String(e),
      });
      setStage({
        kind: 'error',
        message: String((e as Error).message ?? 'Rejection failed.'),
      });
    }
  }

  return (
    <article
      style={{
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        background: 'var(--color-surface)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--space-3)',
        }}
      >
        <div style={{ display: 'grid', gap: 2 }}>
          <strong style={{ fontSize: 'var(--text-body)' }}>{app.name}</strong>
          <span
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {app.email} · {app.phone} · {app.locale.toUpperCase()} · submitted{' '}
            {formatDate(tsMs(app.submittedAt))}
          </span>
        </div>
        <StatusPill status="pending" />
      </header>

      {app.pitch ? (
        <blockquote
          style={{
            margin: 0,
            padding: 'var(--space-3)',
            background: 'var(--tint-subtle)',
            borderLeft: '3px solid var(--color-primary)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-meta)',
            fontStyle: 'italic',
            color: 'var(--color-on-surface)',
            whiteSpace: 'pre-wrap',
          }}
        >
          {app.pitch}
        </blockquote>
      ) : (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            fontStyle: 'italic',
          }}
        >
          No pitch — applicant skipped the optional field.
        </p>
      )}

      {/* Stage-specific surfaces — every state explicit */}
      {stage.kind === 'approved' ? (
        <ApprovedNotice recruiterId={stage.recruiterId} code={stage.code} />
      ) : stage.kind === 'rejected' ? (
        <p
          role="status"
          style={{
            margin: 0,
            padding: 'var(--space-3)',
            background: 'rgba(239,68,68,0.06)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-accent)',
          }}
        >
          Rejected. The applicant won't be notified automatically — reach out via WA if the
          rejection deserves a heads-up.
        </p>
      ) : stage.kind === 'showRejectForm' ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <span style={fieldLabel}>Why are we rejecting? (optional, for audit only)</span>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="e.g., duplicate of an existing recruiter; no carwash network in their area; failed KYC pre-check."
              style={textareaStyle}
            />
          </label>
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setStage({ kind: 'idle' })}
              style={ghostButtonStyle}
            >
              Cancel
            </button>
            <button type="button" onClick={() => void reject()} style={dangerButtonStyle}>
              Confirm rejection
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => setStage({ kind: 'showRejectForm' })}
            disabled={stage.kind === 'approving' || stage.kind === 'rejecting'}
            style={ghostButtonStyle}
          >
            Reject…
          </button>
          <button
            type="button"
            onClick={() => void approve()}
            disabled={stage.kind === 'approving' || stage.kind === 'rejecting'}
            style={{
              ...primaryButtonStyle,
              opacity: stage.kind === 'approving' ? 0.6 : 1,
              cursor: stage.kind === 'approving' ? 'wait' : 'pointer',
            }}
          >
            {stage.kind === 'approving' ? 'Approving…' : 'Approve & mint code'}
          </button>
        </div>
      )}

      {stage.kind === 'error' ? (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: 'var(--space-3)',
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.24)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-accent)',
          }}
        >
          {stage.message} ·{' '}
          <button
            type="button"
            onClick={() => setStage({ kind: 'idle' })}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--color-accent)',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: 'inherit',
            }}
          >
            Dismiss
          </button>
        </p>
      ) : null}
    </article>
  );
}

function ApprovedNotice({
  recruiterId,
  code,
}: {
  readonly recruiterId: string;
  readonly code: string;
}) {
  const [copied, setCopied] = useState<'code' | 'id' | null>(null);

  async function copy(value: string, kind: 'code' | 'id') {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      window.setTimeout(() => setCopied((c) => (c === kind ? null : c)), 1800);
    } catch (e) {
      logger.warn('clipboard copy failed', { error: String(e) });
    }
  }

  return (
    <div
      role="status"
      style={{
        padding: 'var(--space-4)',
        background: 'rgba(13,122,79,0.06)',
        border: '1px solid rgba(13,122,79,0.24)',
        borderRadius: 'var(--radius-sm)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-primary)' }}>
        Approved — recruiter is live.
      </p>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface)',
        }}
      >
        Share the code below with them so they can start signing up agents. They can sign in to{' '}
        <code style={inlineCodeStyle}>agent.kilat.com</code> using the email they applied with.
      </p>
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginTop: 'var(--space-1)',
        }}
      >
        <span style={fieldLabel}>Code:</span>
        <code style={{ ...inlineCodeStyle, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {code}
        </code>
        <button type="button" onClick={() => void copy(code, 'code')} style={chipButtonStyle}>
          {copied === 'code' ? 'Copied!' : 'Copy code'}
        </button>
        <button type="button" onClick={() => void copy(recruiterId, 'id')} style={chipButtonStyle}>
          {copied === 'id' ? 'Copied!' : 'Copy recruiter ID'}
        </button>
      </div>
    </div>
  );
}

// ── pills ──────────────────────────────────────────────────────────────

function StatusPill({ status }: { readonly status: RecruiterApplication['status'] }) {
  const pill =
    status === 'pending'
      ? { bg: 'rgba(245,158,11,0.10)', fg: '#b45309', label: 'Pending' }
      : status === 'approved'
        ? { bg: 'rgba(13,122,79,0.10)', fg: 'var(--color-primary)', label: 'Approved' }
        : { bg: 'rgba(239,68,68,0.10)', fg: 'var(--color-accent)', label: 'Rejected' };
  return (
    <span
      style={{
        background: pill.bg,
        color: pill.fg,
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        fontSize: 'var(--text-meta)',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {pill.label}
    </span>
  );
}

function RecruiterStatusPill({ status }: { readonly status: RecruiterProfile['status'] }) {
  const pill = (() => {
    switch (status) {
      case 'active':
        return { bg: 'rgba(13,122,79,0.10)', fg: 'var(--color-primary)', label: 'Active' };
      case 'pending_kyc':
        return { bg: 'rgba(245,158,11,0.10)', fg: '#b45309', label: 'Pending KYC' };
      case 'suspended':
        return { bg: 'rgba(239,68,68,0.10)', fg: 'var(--color-accent)', label: 'Suspended' };
      case 'terminated':
        return {
          bg: 'var(--color-border)',
          fg: 'var(--color-on-surface-muted)',
          label: 'Terminated',
        };
      case 'applied':
        return {
          bg: 'var(--tint-subtle)',
          fg: 'var(--color-on-surface-muted)',
          label: 'Applied',
        };
    }
  })();
  return (
    <span
      style={{
        background: pill.bg,
        color: pill.fg,
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        fontSize: 'var(--text-meta)',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {pill.label}
    </span>
  );
}

// ── KYC review row (Sprint 87) ─────────────────────────────────────────

function RecruiterKycRow({
  recruiterId,
  kyc,
  currentUserUid,
}: {
  readonly recruiterId: string;
  readonly kyc: RecruiterProfile['kyc'];
  readonly currentUserUid: string;
}) {
  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'verifying' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function markVerified() {
    setStage({ kind: 'verifying' });
    try {
      await recruitersRepo.update(recruiterId, {
        'kyc.verifiedAt': Timestamp.now(),
        'kyc.verifierUid': currentUserUid,
        updatedAt: Timestamp.now(),
      });
      setStage({ kind: 'idle' });
    } catch (e) {
      logger.error('recruiter kyc verify failed', { recruiterId, error: String(e) });
      setStage({
        kind: 'error',
        message: String((e as Error).message ?? 'Could not save.'),
      });
    }
  }

  if (kyc?.verifiedAt) {
    return (
      <div style={kycRowStyle}>
        <span style={{ color: 'var(--color-primary)' }}>
          ✓ KYC verified {formatDate(tsMs(kyc.verifiedAt))}
          {kyc.verifierUid ? ` by ${kyc.verifierUid.slice(0, 8)}` : ''}
        </span>
        {kyc.icScanRef ? <KycScanLink storagePath={kyc.icScanRef} /> : null}
      </div>
    );
  }

  if (kyc?.icScanRef) {
    return (
      <div style={kycRowStyle}>
        <span style={{ color: '#92400e' }}>
          Document submitted{kyc.submittedAt ? ` ${formatDate(tsMs(kyc.submittedAt))}` : ''} ·
          awaiting review
        </span>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <KycScanLink storagePath={kyc.icScanRef} />
          <button
            type="button"
            onClick={() => void markVerified()}
            disabled={stage.kind === 'verifying'}
            style={miniPrimaryButton}
          >
            {stage.kind === 'verifying' ? 'Saving…' : 'Mark verified'}
          </button>
        </div>
        {stage.kind === 'error' ? (
          <span role="alert" style={{ color: 'var(--color-accent)' }}>
            {stage.message}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ ...kycRowStyle, color: 'var(--color-on-surface-muted)' }}>
      <span>No KYC document on file yet.</span>
      <button
        type="button"
        onClick={() => void markVerified()}
        disabled={stage.kind === 'verifying'}
        style={miniGhostButton}
      >
        {stage.kind === 'verifying' ? 'Saving…' : 'Mark verified out-of-band'}
      </button>
      {stage.kind === 'error' ? (
        <span role="alert" style={{ color: 'var(--color-accent)' }}>
          {stage.message}
        </span>
      ) : null}
    </div>
  );
}

const kycRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3)',
  alignItems: 'center',
  flexWrap: 'wrap',
  fontSize: 'var(--text-meta)',
};

const miniPrimaryButton: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
};

const miniGhostButton: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-strong)',
  background: 'transparent',
  color: 'var(--color-on-surface)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
};

// ── helpers ────────────────────────────────────────────────────────────

function isSuperAdminForBrand(claims: Record<string, unknown> | null, brandId: string): boolean {
  if (!claims) return false;
  const sa = (claims as { superadmin?: Record<string, unknown> }).superadmin;
  if (!sa || typeof sa !== 'object') return false;
  return sa[brandId] === true;
}

function tsMs(ts: { toMillis?: () => number; seconds: number } | undefined): number {
  if (!ts) return 0;
  return typeof ts.toMillis === 'function' ? ts.toMillis() : ts.seconds * 1000;
}

function formatDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function SectionHeader({ title, hint }: { readonly title: string; readonly hint: string }) {
  return (
    <div>
      <h2 style={{ fontSize: 'var(--text-h3)', fontWeight: 600, margin: 0 }}>{title}</h2>
      <p
        style={{
          margin: 'var(--space-1) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          maxWidth: 640,
        }}
      >
        {hint}
      </p>
    </div>
  );
}

function EmptyState({ title, body }: { readonly title: string; readonly body: string }) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: 'var(--space-6) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
        justifyItems: 'center',
        marginTop: 'var(--space-4)',
      }}
    >
      <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          maxWidth: 480,
        }}
      >
        {body}
      </p>
    </div>
  );
}

function InlineNote({
  children,
  tone = 'muted',
}: {
  readonly children: React.ReactNode;
  readonly tone?: 'muted' | 'error';
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        margin: 'var(--space-4) 0 0 0',
        padding: 'var(--space-3) var(--space-4)',
        fontSize: 'var(--text-meta)',
        color: tone === 'error' ? 'var(--color-accent)' : 'var(--color-on-surface-muted)',
        background: tone === 'error' ? 'rgba(239,68,68,0.06)' : 'var(--tint-subtle)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {children}
    </p>
  );
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div
        style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'center', maxWidth: 380 }}
      >
        <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {body}
        </p>
        {cta ? (
          <Link
            to={cta.to}
            style={{ ...primaryButtonStyle, textDecoration: 'none', display: 'inline-flex' }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

// ── styles ────────────────────────────────────────────────────────────

const reviewedRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--tint-subtle)',
};

const recruiterRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
};

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const textareaStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border-strong)',
  fontSize: 'var(--text-body)',
  fontFamily: 'inherit',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  width: '100%',
  resize: 'vertical',
  minHeight: 72,
};

const chipButtonStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  padding: '4px 10px',
  border: '1px solid rgba(13,122,79,0.32)',
  borderRadius: 'var(--radius-pill)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
};

const inlineCodeStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, monospace',
  padding: '2px 6px',
  background: 'var(--color-border)',
  borderRadius: 4,
};
